import React from 'react';
import styled from 'styled-components';
import { FormTitle } from '../customComponents/FormTitle';
import { useLang } from '../context/LangContext';
import { formatPriceComponentsByLanguage } from '../utils/currency';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 24px;
`;

const Content = styled.div`
  padding: 16px 0;
`;

// 입찰 정보 컨테이너 (LotPrice와 동일한 스타일)
const BidContainer = styled.div<{ $hasBid: boolean; $isWeb: boolean }>`
  background-color: ${props => {
    if (props.$isWeb) {
      // 웹에서는 입찰이 있을 때만 흰색, 없으면 기본색
      return props.$hasBid ? '#fff' : '#f3f6f6';
    } else {
      // 모바일에서는 입찰 여부에 따라 색상 변경
      return props.$hasBid ? '#f8f5f0' : '#f3f6f6';
    }
  }};
  border-radius: 10px;
  padding: 22px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// 입찰 번호 (왼쪽)
const BidNumber = styled.div`
  font-size: 14px;
  color: #b6a488;
  font-weight: 500;
`;

// 입찰 금액 컨테이너 (오른쪽)
const BidAmount = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

// 환종 스타일
const Currency = styled.span`
  font-size: 12px;
  color: #b6a488;
  font-weight: 500;
`;

// 금액 스타일
const Amount = styled.span`
  font-size: 16px;
  color: #7a6447;
  font-weight: 600;
`;

interface MyBidProps {
  title?: string;
  bidNumber?: string;
  bidAmount?: number;
  hasBid?: boolean; // 입찰 여부 추가
  exchangeRate?: number; // 환율 추가
  isWeb?: boolean; // 웹 여부 추가
}

// 다국어 텍스트
const myBidTexts = {
  ko: {
    title         : '나의 입찰',
    bidNumber     : '#1234',
    noBid         : '입찰 내역이 없습니다'
  },
  en: {
    title         : 'My Bid',
    bidNumber     : '#1234',
    noBid         : 'No bid placed'
  }
};

const MyBid: React.FC<MyBidProps> = ({ 
  title,
  bidNumber,
  bidAmount = 32000000,
  hasBid = false,
  exchangeRate = 1300, // 기본 환율
  isWeb = false // 기본값은 모바일
}) => {
  const { lang } = useLang();
  const texts = myBidTexts[lang];
  
  // props로 받은 값이 없으면 기본 다국어 텍스트 사용
  const sectionTitle = title || texts.title;
  
  // 입찰이 없는 경우
  if (!hasBid) {
    return (
      <Container>
        <FormTitle>{sectionTitle}</FormTitle>
        <Content>
          <BidContainer $hasBid={hasBid} $isWeb={isWeb} style={{ justifyContent: 'center' }}>
            <BidNumber style={{ color: '#999', fontStyle: 'italic' }}>
              {texts.noBid}
            </BidNumber>
          </BidContainer>
        </Content>
      </Container>
    );
  }
  
  // 입찰이 있는 경우
  // bidNumber가 빈 문자열이 아닌 경우에만 사용, 없으면 기본값
  const bidNumberText = bidNumber && bidNumber.trim() !== '' ? bidNumber : texts.bidNumber;
  // bidAmount가 명시적으로 전달되었는지 확인 (0도 유효한 값)
  const finalBidAmount = bidAmount !== undefined ? bidAmount : 32000000;

  // 언어별 단일 통화 표시
  const priceFormat = formatPriceComponentsByLanguage(finalBidAmount, exchangeRate, lang);

  return (
    <Container>
      <FormTitle>{sectionTitle}</FormTitle>
      <Content>
        <BidContainer $hasBid={hasBid} $isWeb={isWeb}>
          <BidNumber>{bidNumberText}</BidNumber>
          <BidAmount>
            <Currency>{priceFormat.currency}</Currency>
            <Amount>{priceFormat.amount}</Amount>
          </BidAmount>
        </BidContainer>
      </Content>
    </Container>
  );
};

export default MyBid;
